import { DAPP_UI_VERSION } from "./version";

/**
 * S3 설정(apps.json)이 "이 버전 미만이면 기능 끔"을 지정할 수 있게 하는 비교기.
 *
 * 비교 대상은 **호스트가 설치한 dapp-ui 버전**(`DAPP_UI_VERSION`)이다. apps.json은
 * 런타임에 CDN에서 읽으므로 새 설정이 구버전 dapp-ui에도 그대로 전달된다 —
 * 즉 구버전에서 깨지는 기능을 배포 없이 끄려면 이 게이트가 필요하다.
 */

type Parsed = { parts: number[]; prerelease: string | null };

/** "1.3.14" / "1.3.14-beta.2" / "v1.3.14" 를 비교 가능한 형태로 만든다. */
function parse(version: string): Parsed | null {
  const match = /^v?(\d+(?:\.\d+)*)(?:-([0-9A-Za-z.-]+))?$/.exec(version.trim());
  if (!match) return null;
  return {
    parts: match[1].split(".").map((part) => Number(part)),
    prerelease: match[2] ?? null,
  };
}

/** semver 순서: a < b 면 음수, 같으면 0, a > b 면 양수. 파싱 실패 시 `null`. */
export function compareVersions(a: string, b: string): number | null {
  const left = parse(a);
  const right = parse(b);
  if (!left || !right) return null;

  const length = Math.max(left.parts.length, right.parts.length);
  for (let index = 0; index < length; index += 1) {
    const diff = (left.parts[index] ?? 0) - (right.parts[index] ?? 0);
    if (diff !== 0) return diff < 0 ? -1 : 1;
  }

  // 1.3.14-beta.1 < 1.3.14 (프리릴리스는 정식 릴리스보다 낮다).
  if (left.prerelease === right.prerelease) return 0;
  if (left.prerelease === null) return 1;
  if (right.prerelease === null) return -1;
  return left.prerelease < right.prerelease ? -1 : 1;
}

/**
 * `minVersion` 요구를 충족하는지. 요구가 없으면 통과.
 *
 * 값이 깨져 있으면(오타·잘못된 형식) **통과시킨다** — 설정 오타 하나로 모든
 * 사용자의 기능이 조용히 사라지는 쪽이 더 위험하다. apps.json 로드 실패 시
 * swap/bridge를 계속 노출하는 기존 정책과 같은 방향이다.
 */
export function meetsMinVersion(
  minVersion?: string,
  currentVersion: string = DAPP_UI_VERSION,
): boolean {
  if (!minVersion) return true;
  const order = compareVersions(currentVersion, minVersion);
  return order === null ? true : order >= 0;
}
